import { useLocation } from "@gatsbyjs/reach-router";
import { getPageByName, getPageByPath } from "@lg-cfa/utils";
import { navigate } from "gatsby";
import { useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { PageNames } from "@enums";
import { useClearData, usePageDataValidator } from "@hooks";
import { currentPageState, pagesState } from "@states";
import {
    weTakeCareActivated,
    weTakeCareReason,
    weTakeCareStepout,
} from "@states/userData";

export function useFlowNavigation() {
    const [page, setCurrentPage] = useRecoilState(currentPageState);
    const setWeTakeCareActivated = useSetRecoilState(weTakeCareActivated);
    const setWeTakeCareStepout = useSetRecoilState(weTakeCareStepout);
    const setWeTakeCareReason = useSetRecoilState(weTakeCareReason);
    const allPages = useRecoilValue(pagesState);
    const { clearAll } = useClearData();
    const location = useLocation();

    const { isValidPage } = usePageDataValidator();

    useEffect(() => {
        const initPage = getPageByPath(
            allPages,
            location.pathname.replace(
                process.env.GATSBY_PATH_PREFIX_CAR ?? "",
                "",
            ),
        );

        if (!initPage) {
            resetFlow();
        } else {
            setCurrentPage(initPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetFlow = () => {
        clearAll();
        navigateTo(PageNames.Welcome, true);
    };

    const navigateTo = (pageName: string, isValidated?: boolean) => {
        const nextPage = getPageByName(allPages, pageName);
        if (!nextPage) return;

        if (!isValidated) {
            isValidated = isValidPage(nextPage);
        }

        if (isValidated) {
            setCurrentPage(nextPage);
            navigate(nextPage.url);
        }
    };

    const next = () => {
        const nextPage = getPageByName(allPages, page?.nextPage);
        if (!nextPage) return;

        if (isValidPage(nextPage)) {
            setCurrentPage(nextPage);
            navigate(nextPage.url);
        }
    };

    const prev = () => {
        const prevPage = getPageByName(allPages, page?.prevPage);
        if (!prevPage) return;

        if (
            prevPage.name !== PageNames.BrokerSearch &&
            prevPage.name !== PageNames.YourInformation
        ) {
            setWeTakeCareActivated(false);
            setWeTakeCareStepout(false);
            setWeTakeCareReason(undefined);
        }

        setCurrentPage(prevPage);
        navigate(prevPage.url);
    };

    return { next, prev, navigateTo, resetFlow };
}
